import React, { useState } from 'react';
import { X, Heart, AlertCircle, Calendar, MapPin, Award, Users, Eye, Trophy, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { getEvaluationsForHorse, getLatestEvaluationForHorse, getAverageScoresForHorse } from '../data/evaluationData';

const HorseImage = ({ horseId, horseName, className, onClick, iconClassName = 'w-6 h-6' }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div
        className={`${className} bg-gray-100 flex items-center justify-center`}
        onClick={onClick}
      >
        <span className={`text-gray-400 font-semibold ${iconClassName}`}>
          {horseName ? horseName.charAt(0).toUpperCase() : '?'}
        </span>
      </div>
    );
  }

  return (
    <div className={`${className} bg-gray-100 overflow-hidden`} onClick={onClick}>
      <img
        src={`https://horseapp.blob.core.windows.net/horses/${horseId}.jpg`}
        alt={horseName}
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

  const HorseModal = ({ horse, isOpen, onClose, onSelectHorse, findHorseById, horseData }) => {
  const [showAllEvaluations, setShowAllEvaluations] = useState(false);
  const [showAllChildren, setShowAllChildren] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  if (!isOpen || !horse) return null;

  const evaluations = getEvaluationsForHorse(horse.id);
  const latestEvaluation = getLatestEvaluationForHorse(horse.id);
  const averageScores = getAverageScoresForHorse(horse.id);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Lifandi': return <Heart className="w-4 h-4 text-green-500" />;
      case 'Fallin': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Lifandi': return 'bg-green-100 text-green-800';
      case 'Fallin': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 9.0) return 'bg-purple-100 text-purple-800';
    if (rating >= 8.5) return 'bg-blue-100 text-blue-800';
    if (rating >= 8.0) return 'bg-green-100 text-green-800';
    if (rating >= 7.5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const renderParentLink = (parentName, parentId) => {
    if (!parentName) return <span className="text-gray-500">Óþekktur</span>;
    
    const parentHorse = findHorseById(parentId);
    if (parentHorse) {
      return (
        <button
          onClick={() => {
            onSelectHorse(parentHorse);
          }}
          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center"
        >
          {parentName}
          <Eye className="w-3 h-3 ml-1" />
        </button>
      );
    }
    return <span className="text-gray-700">{parentName}</span>;
  };

  // Find children of this horse
  const children = horseData.filter(h => h.fatherId === horse.id || h.motherId === horse.id);
  const childrenToShow = showAllChildren ? children : children.slice(0, 5);

  // Get age or death info
  const currentYear = new Date().getFullYear();
  const age = horse.status === 'Lifandi' ? currentYear - horse.year : null;

  const EvaluationCard = ({ evaluation, isLatest = false }) => (
    <div className={`p-4 rounded-lg border ${isLatest ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
      {isLatest && (
        <div className="flex items-center mb-2">
          <Star className="w-4 h-4 text-blue-500 mr-1" />
          <span className="text-sm font-medium text-blue-700">Nýjasta dómur</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <span className="font-medium text-gray-700">Ár:</span> {evaluation.year}
        </div>
        <div>
          <span className="font-medium text-gray-700">Land:</span> {evaluation.judgeCountry}
        </div>
        <div className="md:col-span-2">
          <span className="font-medium text-gray-700">Sýning:</span> {evaluation.competition}
        </div>
        <div>
          <span className="font-medium text-gray-700">Sýnandi:</span> {evaluation.exhibitor}
        </div>
        <div>
          <span className="font-medium text-gray-700">Eigandi:</span> {evaluation.owner}
        </div>
        
        {evaluation.totalScore && (
          <div className="md:col-span-2 mt-2 pt-2 border-t border-gray-300">
            <div className="flex flex-wrap gap-2">
              <span className={`px-2 py-1 text-xs font-semibold rounded ${getRatingColor(evaluation.totalScore)}`}>
                Aðaleinkunn: {evaluation.totalScore}
              </span>
              {evaluation.conformation && (
                <span className={`px-2 py-1 text-xs font-semibold rounded ${getRatingColor(evaluation.conformation)}`}>
                  Sköpulag: {evaluation.conformation}
                </span>
              )}
              {evaluation.abilities && (
                <span className={`px-2 py-1 text-xs font-semibold rounded ${getRatingColor(evaluation.abilities)}`}>
                  Hæfileikar: {evaluation.abilities}
                </span>
              )}
            </div>
            
            {evaluation.genetics && (
              <div className="mt-2">
                <span className="text-xs text-gray-600">
                  Arfgerð: <span className="font-medium">{evaluation.genetics}</span>
                  {evaluation.geneticAnalysis && ` (${evaluation.geneticAnalysis})`}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const ChildrenTable = ({ children }) => {
    if (children.length === 0) return null;

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Nafn</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Ár</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Staða</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Kynbótamat</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Dómar</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Land</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Aðgerð</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {children.map(child => {
              const childLatestEvaluation = getLatestEvaluationForHorse(child.id);
              const childAverageScores = getAverageScoresForHorse(child.id);
              const childAge = child.status === 'Lifandi' ? currentYear - child.year : null;
              
              return (
                <tr key={child.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div>
                      <div className="font-medium text-gray-900">{child.name}</div>
                      <div className="text-xs text-gray-500">{child.id}</div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div>
                      <div className="text-gray-900">{child.year}</div>
                      {childAge && (
                        <div className="text-xs text-gray-500">{childAge} vetra</div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      {getStatusIcon(child.status)}
                      <span className={`ml-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(child.status)}`}>
                        {child.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {child.rating > 0 && (
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRatingColor(child.rating)}`}>
                        {child.rating}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="space-y-1">
                      {childLatestEvaluation && childLatestEvaluation.totalScore && (
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-blue-500" />
                          <span className={`px-1 py-0.5 text-xs font-semibold rounded ${getRatingColor(childLatestEvaluation.totalScore)}`}>
                            {childLatestEvaluation.totalScore}
                          </span>
                          <span className="text-xs text-gray-500">({childLatestEvaluation.year})</span>
                        </div>
                      )}
                      {childAverageScores && (
                        <div className="flex items-center space-x-1">
                          <Trophy className="w-3 h-3 text-green-500" />
                          <span className={`px-1 py-0.5 text-xs font-semibold rounded ${getRatingColor(parseFloat(childAverageScores.totalScore))}`}>
                            ⌀{childAverageScores.totalScore}
                          </span>
                          <span className="text-xs text-gray-500">({childAverageScores.evaluationCount})</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-gray-700">{child.country}</span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => onSelectHorse(child)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Skoða nánar"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
{/* Header */}
<div className="flex items-center justify-between p-6 border-b border-gray-200">
  <div className="flex items-center space-x-4">
    
    {/* Mynd af hesti */}
    <HorseImage
      horseId={horse.id}
      horseName={horse.name}
      className="w-24 h-24 rounded-lg flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={() => setShowImageLightbox(true)}
      iconClassName="text-2xl"
    />

    <div>
      <h2 className="text-2xl font-bold text-gray-900">{horse.name}</h2>
              <p className="text-sm text-gray-500">{horse.origin}</p>
              <p className="text-xs text-gray-400">{horse.id}</p>
            </div>
            <div className="flex items-center space-x-2">
              {getStatusIcon(horse.status)}
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(horse.status)}`}>
                {horse.status}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                Grunnupplýsingar
              </h3>
              
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Fæðingarár:</span>
                  <span className="text-sm text-gray-900">{horse.year}</span>
                </div>
                
                {age && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Aldur:</span>
                    <span className="text-sm text-gray-900">{age} vetra</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Land:</span>
                  <span className="text-sm text-gray-900">{horse.country}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Svæði:</span>
                  <span className="text-sm text-gray-900">{horse.region}</span>
                </div>
                
                {horse.colorCode && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Litkóði:</span>
                    <span className="text-sm text-gray-900">{horse.colorCode}</span>
                  </div>
                )}
              </div>

              {/* Original Rating */}
              {horse.rating > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center mb-2">
                    <Award className="w-5 h-5 mr-2 text-yellow-500" />
                    Kynbótamat
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <span className={`inline-flex px-4 py-2 text-lg font-bold rounded-full ${getRatingColor(horse.rating)}`}>
                      {horse.rating}
                    </span>
                  </div>
                </div>
              )}


              {/* Average Scores */}
              {averageScores && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center mb-2">
                    <Trophy className="w-5 h-5 mr-2 text-green-500" />
                    Meðaleinkunn dóma
                  </h4>
                  <div className="bg-green-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Aðaleinkunn:</span>
                      <span className={`px-2 py-1 text-sm font-semibold rounded ${getRatingColor(parseFloat(averageScores.totalScore))}`}>
                        {averageScores.totalScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Sköpulag:</span>
                      <span className={`px-2 py-1 text-sm font-semibold rounded ${getRatingColor(parseFloat(averageScores.conformation))}`}>
                        {averageScores.conformation}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Hæfileikar:</span>
                      <span className={`px-2 py-1 text-sm font-semibold rounded ${getRatingColor(parseFloat(averageScores.abilities))}`}>
                        {averageScores.abilities}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2">
                      Byggt á {averageScores.evaluationCount} dómum
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Family Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2 text-green-500" />
                Ætterni
              </h3>
              
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500 block mb-1">Faðir:</span>
                  {renderParentLink(horse.father, horse.fatherId)}
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-500 block mb-1">Móðir:</span>
                  {renderParentLink(horse.mother, horse.motherId)}
                </div>
              </div>
            </div>

            {/* Evaluations */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-purple-500" />
                Dómar ({evaluations.length})
              </h3>

              {evaluations.length > 0 ? (
                <div className="space-y-3">
                  {/* Latest Evaluation */}
                  {latestEvaluation && (
                    <EvaluationCard evaluation={latestEvaluation} isLatest={true} />
                  )}

                  {/* Other Evaluations */}
                  {evaluations.length > 1 && (
                    <div>
                      <button
                        onClick={() => setShowAllEvaluations(!showAllEvaluations)}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
                      >
                        {showAllEvaluations ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Fela aðra dóma
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Sýna alla dóma ({evaluations.length - 1} til viðbótar)
                          </>
                        )}
                      </button>

                      {showAllEvaluations && (
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {evaluations
                            .filter(evaluation => evaluation !== latestEvaluation)
                            .sort((a, b) => b.year - a.year)
                            .map((evaluation, index) => (
                              <EvaluationCard key={index} evaluation={evaluation} />
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                  Engir dómar skráðir
                </div>
              )}
            </div>
          </div>

          {/* Children Section */}
          {children.length > 0 && (
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-orange-500" />
                  Afkvæmi ({children.length})
                </h3>
                {children.length > 5 && (
                  <button
                    onClick={() => setShowAllChildren(!showAllChildren)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAllChildren ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-1" />
                        Sýna færri
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-1" />
                        Sýna öll ({children.length - 5} til viðbótar)
                      </>
                    )}
                  </button>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <ChildrenTable children={childrenToShow} />
              </div>
            </div>
          )}
        </div>

      {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Loka
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Lightbox */}
    {showImageLightbox && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] cursor-pointer"
        onClick={() => setShowImageLightbox(false)}
      >
        <div className="relative max-w-2xl max-h-[80vh] p-4">
          <HorseImage
            horseId={horse.id}
            horseName={horse.name}
            className="max-w-full max-h-[75vh] rounded-lg flex items-center justify-center min-w-[200px] min-h-[200px]"
            iconClassName="text-5xl"
          />
          <button
            onClick={() => setShowImageLightbox(false)}
            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-80"
          >
            <X className="w-5 h-5" />
          </button>
          <p className="text-white text-center mt-2 text-sm">{horse.name}</p>
        </div>
      </div>
    )}
  </>
  );

};

export default HorseModal;
